// src/components/layouts/AuthLayout.jsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MinimalLogo from '@/components/ui/MinimalLogo';
import { useTenant } from '@/contexts/TenantContext';

const AuthLayout = ({ children, heading, subheading }) => {
  const location = useLocation();
  const { tenant, branding, loading } = useTenant();

  // Is this a partner tenant (not the main app)?
  const isPartner = tenant && !tenant.is_aggregator && tenant.slug !== 'app';

  // Pull branding values — fall back gracefully to Zelospace defaults
  const logoUrl      = branding?.logo_url      || null;
  const primaryColor = branding?.primary_colour || null;
  const tagline      = branding?.tagline        || null;

  return (
    <div
      className="min-h-screen w-screen flex items-center justify-center px-4"
      style={{
        background: branding?.bg_colour
          ? branding.bg_colour
          : undefined,
      }}
      // Falls back to Tailwind bg-stone-brown if no custom bg
    >
      <div
        className={`w-full max-w-md rounded-xl shadow-xl p-8 ${
          !branding?.bg_colour ? 'bg-stone-brown' : ''
        }`}
      >
        <div
          className="w-full bg-raisin-black rounded-xl shadow-xl p-8"
        >

          {/* ── Top nav row ─────────────────────────────────────────── */}
          <div className="flex justify-between items-center mb-4">
            {/* Hide Zelospace logo on partner subdomains — guests don't know what it is */}
            {!isPartner && <MinimalLogo className="h-10 -ml-[4px]" />}
            {isPartner && <div />}

            {location.pathname === '/login' && (
              <Link
                to="/signup"
                className="text-sm text-swan font-medium hover:underline hover:text-samson-blue"
              >
                Sign Up
              </Link>
            )}
            {location.pathname === '/signup' && (
              <Link
                to="/login"
                className="text-sm text-swan font-medium hover:underline hover:text-samson-blue"
              >
                Log In
              </Link>
            )}
          </div>

          {/* ── Partner branding block (signup + login) ──────────────── */}
          <AnimatePresence>
            {isPartner && !loading && (
              <motion.div
                key="partner-branding"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="mb-6 text-center"
              >
                {/* Partner logo */}
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt={`${tenant.name} logo`}
                    className="h-14 w-auto mx-auto mb-4 object-contain"
                    onError={e => {
                      console.warn('[AuthLayout] Logo failed to load:', logoUrl);
                      e.target.style.display = 'none';
                    }}
                  />
                )}

                {/* Welcome heading */}
                <h1
                  className="text-xl font-bold mb-3"
                  style={{ color: primaryColor || '#c8f04a' }}
                >
                  Welcome to {tenant.name}
                </h1>

                {/* Optional tagline from branding config */}
                {tagline && (
                  <p className="text-sm text-swan/70 mt-2 mb-1">{tagline}</p>
                )}

                {/* Context line — helps first-time guests understand what they're signing up for */}
                {isPartner && location.pathname === '/signup' && (
                  <p className="text-xs text-swan/40 mt-2">
                    Create a free account to view and vote on photos from the night.
                  </p>
                )}

                {/* Divider */}
                <div
                  className="mt-5 h-px w-full opacity-20"
                  style={{ background: primaryColor || '#c8f04a' }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Page heading (e.g. "Create Account") ─────────────────── */}
          {heading && (
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-white text-left font-poppins">
                {heading}
              </h2>
              {subheading && (
                <p className="text-sm text-swan/60 mt-1">{subheading}</p>
              )}
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;